import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
  EllipsisVerticalIcon,
  PencilIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  ShareIcon,
  DocumentIcon,
} from '@heroicons/react/24/outline';
import Card from '../../../../ui/canon/Card';

const DocumentCard = ({
  document,
  onEdit,
  onDelete,
  onDownload,
  onView,
  onToggleShare,
}) => {
  const intl = useIntl();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      window.document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      window.document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const handleMenuToggle = (e) => {
    e.stopPropagation();
    setMenuOpen(!menuOpen);
  };

  const handleMenuAction = (action) => {
    setMenuOpen(false);
    action();
  };

  return (
    <Card
      variant="outlined"
      size="md"
      className="h-full cursor-pointer transition-all duration-200 hover:shadow-succession-lg hover:-translate-y-1 overflow-hidden"
      onClick={onView}
    >
      <div className="flex flex-col h-full w-full overflow-hidden">
        <div className="flex justify-between items-start mb-2">
          <div className="p-2 bg-succession-bg-secondary rounded-md">
            <DocumentIcon className="h-5 w-5 text-succession-text-secondary" />
          </div>
          <div className="relative" ref={menuRef}>
            <button
              onClick={handleMenuToggle}
              className="p-1 text-succession-text-secondary hover:text-succession-text-primary hover:bg-succession-hover-bg rounded-md transition-theme duration-theme"
            >
              <EllipsisVerticalIcon className="h-5 w-5" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-8 z-50 w-48 bg-succession-bg-primary border border-succession-border rounded-md shadow-succession-lg py-1">
                <button
                  onClick={() => handleMenuAction(onView)}
                  className="w-full px-4 py-2 text-left text-sm text-succession-text-primary hover:bg-succession-hover-bg flex items-center gap-2 transition-theme duration-theme"
                >
                  <EyeIcon className="h-4 w-4" />
                  {intl.formatMessage({ id: 'succession.vault.action_view' })}
                </button>
                <button
                  onClick={() => handleMenuAction(onDownload)}
                  className="w-full px-4 py-2 text-left text-sm text-succession-text-primary hover:bg-succession-hover-bg flex items-center gap-2 transition-theme duration-theme"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  {intl.formatMessage({ id: 'succession.vault.action_download' })}
                </button>
                <button
                  onClick={() => handleMenuAction(onEdit)}
                  className="w-full px-4 py-2 text-left text-sm text-succession-text-primary hover:bg-succession-hover-bg flex items-center gap-2 transition-theme duration-theme"
                >
                  <PencilIcon className="h-4 w-4" />
                  {intl.formatMessage({ id: 'succession.vault.action_edit' })}
                </button>
                {document.can_delete && (
                  <button
                    onClick={() => handleMenuAction(onDelete)}
                    className="w-full px-4 py-2 text-left text-sm text-succession-text-primary hover:bg-succession-hover-bg flex items-center gap-2 text-red-600 dark:text-red-400 transition-theme duration-theme"
                  >
                    <TrashIcon className="h-4 w-4" />
                    {intl.formatMessage({ id: 'succession.vault.action_delete' })}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <h3 className="font-semibold text-base mb-2 line-clamp-2 text-succession-text-primary">
          {document.title}
        </h3>

        {document.description && (
          <p className="text-sm text-succession-text-secondary mb-2 line-clamp-2">
            {document.description}
          </p>
        )}

        {document.source_type === 'sprint_item' && document.sprint_name && (
          <p className="text-xs text-succession-text-muted mb-2">
            {intl.formatMessage({ id: 'succession.vault.sprint_label' }, { name: document.sprint_name })}
          </p>
        )}

        {document.tags && document.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {document.tags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="px-2 py-0.5 text-xs bg-succession-bg-secondary text-succession-text-secondary rounded-md"
              >
                {tag}
              </span>
            ))}
            {document.tags.length > 3 && (
              <span className="px-2 py-0.5 text-xs bg-succession-bg-secondary text-succession-text-secondary rounded-md">
                +{document.tags.length - 3}
              </span>
            )}
          </div>
        )}

        <div className="flex justify-between items-center mt-auto pt-3 border-t border-succession-border gap-2">
          <span className="text-xs text-succession-text-secondary truncate min-w-0 flex-1">
            {document.file_name || document.document?.name || intl.formatMessage({ id: 'succession.vault.unknown_file' })}
          </span>
          <span className="text-xs text-succession-text-secondary whitespace-nowrap">
            {formatFileSize(document.file_size || document.document?.file_size)}
          </span>
        </div>

        <div className="mt-3 pt-3 border-t border-succession-border">
          <label className="flex items-center gap-2 cursor-pointer" onClick={(e) => e.stopPropagation()}>
            <div className="relative inline-block w-10 h-5">
              <input
                type="checkbox"
                checked={document.shared_with_negotiating || false}
                onChange={(e) => {
                  e.stopPropagation();
                  onToggleShare(document, e.target.checked);
                }}
                className="sr-only"
              />
              <div
                className={`absolute inset-0 rounded-full transition-all duration-200 ${
                  document.shared_with_negotiating
                    ? 'bg-succession-primary'
                    : 'bg-succession-bg-secondary'
                }`}
              />
              <div
                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform duration-200 ${
                  document.shared_with_negotiating ? 'translate-x-5' : ''
                }`}
              />
            </div>
            <div className="flex items-center gap-1 text-sm text-succession-text-secondary">
              <ShareIcon className="h-4 w-4" />
              <span>{intl.formatMessage({ id: 'succession.vault.action_share' })}</span>
            </div>
          </label>
        </div>
      </div>
    </Card>
  );
};

DocumentCard.propTypes = {
  document: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  onToggleShare: PropTypes.func.isRequired,
};

export default DocumentCard;
